import React, { useEffect } from 'react';

import combinedActions from '~redux/combinedActions';
import { useAppDispatch, useAppSelector } from '~redux/hooks';
import { initialState as adminInitialState } from '~redux/slices/admin';

import SearchAndFilterBar, { TSearchAndFilterBarProps } from '~components/molecules/SearchAndFilterBar';

import { TGetAdminListingFilters } from '~types/feat/admin';

import { AuthUserStatusEnum, BranchStatusEnum } from '~utils/enums';
import * as Styles from './_adminsSearchAndFilterBar.module.scss';

const AdminsSearchAndFilterBar = () => {
    const filters = useAppSelector(state => state.admin.adminListing.filters);
    const branchesList = useAppSelector(state => state.branch.allBranches);
    const { allBranchAccess, branchCode } = useAppSelector(state => state.auth.userDetails);
    const branchOptions = branchesList.filter(branch => branch.status === BranchStatusEnum.Active);

    const initialFilters = adminInitialState.adminListing.filters;

    const dispatch = useAppDispatch();

    const setFilters = (inputFilters: TGetAdminListingFilters) => dispatch(combinedActions.adminSetGetAdminListingFilters(inputFilters));
    const handleSearchClick = () => dispatch(combinedActions.adminGetAdminListingAttempt());
    const handleCancelClick = () => {
        const currentBranch = branchesList.find(branch => branch.branchCode === branchCode);

        setFilters({
            ...initialFilters,
            branchId: !allBranchAccess ? currentBranch?.branchId || 0 : 0,
            branchCode: !allBranchAccess ? branchCode : '',
            branchName: !allBranchAccess ? currentBranch?.branchName || '' : '',
        });

        handleSearchClick();
    };

    useEffect(() => {
        if (branchesList.length > 0 && !allBranchAccess) {
            const currentBranch = branchesList.find(branch => branch.branchCode === branchCode);
            setFilters({
                ...filters,
                branchCode,
                branchId: currentBranch?.branchId || 0,
                branchName: branchesList.find((branch) => branch.branchCode === branchCode)?.branchName || '',
            });
        }
    }, [branchesList]);

    const massagedBranchesListOption = () => {
        if (!branchesList.length) return [];

        return branchesList.map((branch) => ({
            label: branch.branchName,
            value: branch.branchId,
        }));
    };

    const fields: TSearchAndFilterBarProps['fields'] = [
        {
            label: 'Email:',
            type: 'input' as const,
            inputParams: {
                value: filters.email,
                setValue: (event) => setFilters({ ...filters, email: event.target.value }),
                onKeyDown: (event) => { if (event.key === 'Enter') handleSearchClick(); },
                className: Styles.input,
            },
        },
        {
            label: 'Branch:',
            type: 'dropdown' as const,
            dropdownParams: {
                options: massagedBranchesListOption(),
                value: filters.branchName,
                setValue: (event: React.ChangeEvent<HTMLInputElement>) => {
                    const selectedBranch = branchOptions.find(branch => branch.branchName === event.target.value);

                    setFilters({
                        ...filters,
                        branchName: event.target.value,
                        branchCode: selectedBranch?.branchCode || '',
                        branchId: selectedBranch?.branchId || 0,
                    });
                },
                optionSelected: !!branchOptions.find((branch) => branch.branchName === filters.branchName),
                noOptionsMessage: 'No branch found',
                disabled: !allBranchAccess,
            },
        },
        {
            label: 'Status:',
            type: 'dropdown' as const,
            dropdownParams: {
                options: [
                    {
                        label: 'Active',
                        value: AuthUserStatusEnum.Active,
                    },
                    {
                        label: 'Deactivated',
                        value: AuthUserStatusEnum.Deactivated,
                    },
                ],
                value: filters.status,
                setValue: (event) => setFilters({ ...filters, status: event.target.value }),
                optionSelected: !!AuthUserStatusEnum[filters.status as keyof typeof AuthUserStatusEnum],
                noOptionsMessage: 'No status found',
                className: Styles.input,
            },
        },
    ];

    return (
        <SearchAndFilterBar
            fields={fields}
            searchOnClick={handleSearchClick}
            cancelOnClick={handleCancelClick}
            containerClassName={Styles['content-container']}
        />
    );
};

export default AdminsSearchAndFilterBar;
